/**
 * Status-taxonomy color helpers (ADR-057 v1.1).
 *
 * Kanban columns own a `color` field. The user picks from a palette of
 * 10 soft Notion-style tints. These helpers expose:
 *   - STATUS_COLORS: the canonical list of color keys + display labels.
 *   - colorToTint(): returns Tailwind-friendly inline-style props
 *     (`backgroundColor` + `color`) computed from the CSS variables in
 *     tokens.css. Why inline-style and not Tailwind classes? Because
 *     Tailwind doesn't ship per-status arbitrary-class composition,
 *     and we want runtime-resolvable theming (dark mode flips
 *     automatically through the same `--status-*` variables).
 *
 * Components import `colorToTint(column.color)` and spread the result
 * onto the styled element.
 */

export const STATUS_COLORS = [
  'default',
  'gray',
  'brown',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
] as const;

export type StatusColor = (typeof STATUS_COLORS)[number];

/** Human-readable labels for the color picker. */
export const STATUS_COLOR_LABELS: Record<StatusColor, string> = {
  default: 'Default',
  gray: 'Gray',
  brown: 'Brown',
  orange: 'Orange',
  yellow: 'Yellow',
  green: 'Green',
  blue: 'Blue',
  purple: 'Purple',
  pink: 'Pink',
  red: 'Red',
};

interface ColorTint {
  /** `hsl(var(--status-X-bg))` — for surfaces (column wash, pill bg). */
  background: string;
  /** `hsl(var(--status-X-fg))` — for dots / text / icons on that color. */
  foreground: string;
}

/**
 * Resolve a stored color key to the CSS variable references. Returns
 * neutral muted-foreground for `default` / null so callers can blast
 * the result onto any element without branching.
 */
export function colorTint(color: StatusColor | string | null | undefined): ColorTint {
  const key = normaliseColor(color);
  if (key === 'default') {
    return {
      background: 'transparent',
      foreground: 'hsl(var(--color-muted-foreground))',
    };
  }
  return {
    background: `hsl(var(--status-${key}-bg))`,
    foreground: `hsl(var(--status-${key}-fg))`,
  };
}

/** Lower-cased palette member or `default`. */
export function normaliseColor(value: string | null | undefined): StatusColor {
  if (!value) return 'default';
  const lower = value.toLowerCase();
  return (STATUS_COLORS as readonly string[]).includes(lower) ? (lower as StatusColor) : 'default';
}
